import { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Spin, message } from 'antd';
import ReactEChartsCore from 'echarts-for-react';
import { photovoltaic } from '../../../services/api';

export default function Photovoltaic() {
  const [loading, setLoading] = useState(true);
  const [systems, setSystems] = useState([]);
  const [prediction, setPrediction] = useState(null);

  useEffect(() => {
    Promise.all([photovoltaic.list(), photovoltaic.prediction(), photovoltaic.realtime()])
      .then(([listRes, predRes, rtRes]) => {
        const rtMap = Object.fromEntries((rtRes.data?.systems || []).map(s => [s.id, s]));
        setSystems((listRes.data || []).map(sys => {
          const rt = rtMap[sys.id];
          return {
            key: sys.id,
            name: sys.name,
            capacity: parseFloat(sys.installed_capacity),
            panels: sys.panel_count,
            power: rt ? rt.real_time_power : 0,
            daily: rt ? rt.daily_yield : 0,
            total: rt ? rt.total_yield : null,
            efficiency: rt ? rt.efficiency : 0,
            status: sys.is_active ? (rt?.status || '待机') : '停机',
          };
        }));
        setPrediction(predRes.data);
      })
      .catch(err => message.error(err.message || '加载光伏数据失败'))
      .finally(() => setLoading(false));
  }, []);

  const hourly = prediction?.hourly || [];
  const chartOption = {
    tooltip: { trigger: 'axis' },
    legend: { data: ['预测发电量', '置信上限', '置信下限'] },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: hourly.map(h => `${h.hour}:00`) },
    yAxis: { type: 'value', name: 'kW' },
    series: [
      { name: '预测发电量', type: 'line', smooth: true, data: hourly.map(h => h.predicted_power), lineStyle: { type: 'dashed' }, itemStyle: { color: '#52c41a' } },
      { name: '置信上限', type: 'line', smooth: true, data: hourly.map(h => h.confidence_upper), lineStyle: { opacity: 0.3 }, symbol: 'none', itemStyle: { color: '#52c41a' } },
      { name: '置信下限', type: 'line', smooth: true, data: hourly.map(h => h.confidence_lower), lineStyle: { opacity: 0.3 }, symbol: 'none', itemStyle: { color: '#52c41a' } },
    ]
  };

  const columns = [
    { title: '光伏阵列', dataIndex: 'name' },
    { title: '装机容量(kWp)', dataIndex: 'capacity' },
    { title: '组件数量', dataIndex: 'panels' },
    { title: '实时功率(kW)', dataIndex: 'power', render: v => v.toFixed(1), sorter: (a, b) => a.power - b.power },
    { title: '日发电量(kWh)', dataIndex: 'daily', render: v => Math.round(v).toLocaleString() },
    { title: '累计发电量(kWh)', dataIndex: 'total', render: v => v == null ? '-' : Math.round(v).toLocaleString() },
    { title: '效率(%)', dataIndex: 'efficiency', render: v => `${v.toFixed(1)}%` },
    { title: '状态', dataIndex: 'status', render: v => <Tag color={v === '运行' ? 'green' : 'default'}>{v}</Tag> },
  ];

  const totalCapacity = systems.reduce((s, x) => s + x.capacity, 0);
  const totalPower = systems.reduce((s, x) => s + x.power, 0);
  const totalDaily = systems.reduce((s, x) => s + x.daily, 0);

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col span={6}><Card size="small"><Statistic title="总装机容量" value={totalCapacity} suffix="kWp" valueStyle={{ color: '#1890ff' }} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="实时总功率" value={totalPower} suffix="kW" precision={1} valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="今日发电量" value={totalDaily} suffix="kWh" precision={0} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="预测明日总发电" value={prediction?.total_predicted || 0} suffix="kWh" precision={0} /></Card></Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}><Card title={`光伏发电预测 (${prediction?.date || '明日'})`} size="small"><ReactEChartsCore option={chartOption} style={{ height: 280 }} /></Card></Col>
      </Row>
      <Card title="光伏系统列表" style={{ marginTop: 16 }} size="small">
        <Table columns={columns} dataSource={systems} pagination={false} size="small" />
      </Card>
    </div>
  );
}
